import { ComparisonOperator, RequestQueryBuilder } from '@nestjsx/crud-request';
import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosResponse,
} from 'axios';
import { checkIsBrowser, createReactQueryFns, isNil } from '@alpha.apps/react-common';

import cloneDeep from 'lodash/cloneDeep';
import router from 'next/router';
import { storage } from '../storage';
import getConfig from 'next/config';

const getLangHeader = (validLang: string): AxiosRequestHeaders =>
  validLang
    ? {
        'Accept-Language': validLang.toUpperCase(),
      }
    : null;

const getTokenHeader = (accessToken: string): AxiosRequestHeaders =>
  accessToken
    ? {
        'Authorization': accessToken,
      }
    : null;

const replaceAllQueryParams = (
  url: AxiosRequestConfig['url'],
  query: AxiosRequestConfig['query']
) => {
  return url && query
    ? Object.entries(query).reduce((a, [k, v]) => {
        const replacer = new RegExp(`/:${k}`, 'g');

        return !isNil(v) ? a.replace(replacer, `/${encodeURIComponent(v)}`) : a;
      }, url)
    : url;
};

const getNestCrudQuery = (config: any) => {
  const qb = RequestQueryBuilder.create();
  if (config && config.pagination) {
    qb.setPage(config.pagination.page);
    qb.setLimit(config.pagination.rowsPerPage);
  }
  if (config && config.sort) {
    qb.sortBy({
      field: config.sort.field,
      order: config.sort.order,
    });
  }
  if (config && config.join) {
    const joinArr = config.join.map((item: string) => ({ field: item }));
    qb.setJoin(joinArr);
  }
  if (config && config.filters) {
    const filters = cloneDeep(config.filters);
    Object.keys(filters).forEach(filterProperty => {
      Object.keys(filters[filterProperty]).forEach(filterOperator => {
        if (
          filters[filterProperty][filterOperator] === null ||
          filters[filterProperty][filterOperator] === undefined ||
          filters[filterProperty][filterOperator] === ''
        ) {
          delete filters[filterProperty][filterOperator];
        } else {
          qb.setFilter({
            field: filterProperty,
            operator: filterOperator as ComparisonOperator,
            value: filters[filterProperty][filterOperator],
          });
        }
      });
    });
  }
  return { ...config, params: { ...config.params, ...qb.queryObject } };
};

const handleTimeout = (error: AxiosError): Promise<any> => {
  return Promise.reject(
    error.message === 'TIMEOUT' ? { ...error, code: 'ETIMEDOUT' } : error
  );
};

const useClearTimeout = (response: AxiosResponse): AxiosResponse => {
  clearTimeout(response.config['timeoutId']);
  return response;
};
const { publicRuntimeConfig } = getConfig();

export const axiosInstance = axios.create({
  baseURL: process.env.NEXT_APP_API_URL,
  timeout: 9000,
});

axiosInstance.interceptors.request.use(
  config => {
    let validLang = null;

    if (checkIsBrowser()) {
      const lang =
        storage?.user?.get()?.language ||
        storage?.guest?.get()?.language ||
        router?.locale;

      const upperCaseLang = lang?.toUpperCase();
      if (upperCaseLang === undefined || upperCaseLang === null) {
        validLang = null;
      } else if (upperCaseLang?.includes('AR')) {
        validLang = 'AR';
      } else if (upperCaseLang?.includes('EN')) {
        validLang = 'EN';
      } else {
        validLang = 'EN';
      }
    } else {
      validLang = null;
    }

    const userToken = storage?.user?.get()?.token;
    const accessToken = userToken ? `Bearer ${userToken}` : null;

    config.headers = {
      ...config.headers,
      'x-device-platform': 'website',
      ...getLangHeader(validLang),
      ...getTokenHeader(accessToken),
    };

    config.url = replaceAllQueryParams(config.url, config.query);

    return getNestCrudQuery(config);
  },
  err => {
    Promise.reject(err);
  }
);

axios.interceptors.response.use(useClearTimeout, handleTimeout);

/* Creating a bunch of functions that we can use to make requests. */
export const { createMutation, createQuery, createShared, createInfiniteQuery } =
  createReactQueryFns({
    axiosInstance,
  });

export type QueryKeyType = {
  url: string;
};

export const setHeaderToken = (token: string) => {
  axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
};

export const removeHeaderToken = () => {
  delete axiosInstance.defaults.headers.common.Authorization;
};
